import { zodResolver } from '@hookform/resolvers/zod';
import type {
  ChannelsChannelResponse,
  ChannelsCreateChannelRequestChannelType,
  ChannelsCreateChannelRequestProvider,
} from '@smarthome/api';
import {
  AuthRole,
  ChannelsCreateChannelRequestChannelType as ChannelType,
  useListChannels,
  useCreateChannel,
  useUpdateChannel,
  useDeleteChannel,
  useTestChannel,
  useMe,
  useListTwilioMessagingServices,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Mail,
  MessageSquare,
  GripVertical,
  Send,
  Shield,
  RefreshCw,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  TextField,
  PasswordField,
  Toggle,
  Modal,
  SlideOutPanel,
  Badge,
  useToast,
  Checkbox,
  Select,
} from '@/components/ui';

// Provider display info
const PROVIDER_INFO: Record<
  ChannelsCreateChannelRequestProvider,
  { label: string; channelType: ChannelsCreateChannelRequestChannelType }
> = {
  smtp: { label: 'SMTP', channelType: ChannelType.email },
  sendgrid: { label: 'SendGrid', channelType: ChannelType.email },
  postmark: { label: 'Postmark', channelType: ChannelType.email },
  twilio: { label: 'Twilio', channelType: ChannelType.sms },
};

// Channel type display info
const CHANNEL_TYPE_INFO: Record<
  ChannelsCreateChannelRequestChannelType,
  { label: string; icon: React.ReactNode }
> = {
  email: { label: 'Email', icon: <Mail className="h-4 w-4" /> },
  sms: { label: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
};

// Base form schema (common fields)
const baseSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(255),
  priority: z.number().min(0, 'Priority must be 0 or greater'),
  isEnabled: z.boolean(),
});

// Provider-specific schemas
const smtpConfigSchema = z.object({
  host: z.string().min(1, 'Host is required'),
  port: z.number().min(1, 'Port must be between 1-65535').max(65535),
  username: z.string().optional(),
  password: z.string().optional(),
  fromAddress: z.string().email('Invalid email address'),
  fromName: z.string().min(1, 'From name is required'),
  useTLS: z.boolean(),
});

const sendgridConfigSchema = z.object({
  apiKey: z.string().optional(),
  fromAddress: z.string().email('Invalid email address'),
  fromName: z.string().min(1, 'From name is required'),
});

const postmarkConfigSchema = z.object({
  serverToken: z.string().optional(),
  fromAddress: z.string().email('Invalid email address'),
  fromName: z.string().min(1, 'From name is required'),
});

const twilioConfigSchema = z
  .object({
    accountSid: z.string().min(1, 'Account SID is required'), // Always required (used in API URL)
    authToken: z.string().optional(), // Used for Account auth
    apiKeySid: z.string().optional(), // Used for API Key auth (starts with SK)
    apiKeySecret: z.string().optional(), // Used for API Key auth
    authMethod: z.enum(['account', 'apikey']).optional(),
    region: z.enum(['us1', 'ie1', 'au1']).optional(),
    senderType: z.enum(['number', 'messaging_service']).optional(),
    fromNumber: z.string().optional(),
    messagingServiceSid: z.string().optional(),
  })
  .refine(
    (data) => {
      const authMethod = data.authMethod ?? 'account';
      if (authMethod === 'account') {
        return !!data.authToken;
      }
      return !!data.apiKeySid && !!data.apiKeySecret;
    },
    {
      message: 'Please provide valid credentials',
      path: ['authToken'],
    }
  )
  .refine(
    (data) => {
      const senderType = data.senderType ?? 'number';
      if (senderType === 'number') {
        return !!data.fromNumber;
      }
      return !!data.messagingServiceSid;
    },
    {
      message: 'Please provide a sender (phone number or messaging service)',
      path: ['fromNumber'],
    }
  );

// Twilio region options
const TWILIO_REGIONS = [
  { value: 'us1', label: 'United States' },
  { value: 'ie1', label: 'Ireland' },
  { value: 'au1', label: 'Australia' },
] as const;

// Combined form data type
type SmtpFormData = z.infer<typeof baseSchema> & {
  provider: 'smtp';
  config: z.infer<typeof smtpConfigSchema>;
};
type SendgridFormData = z.infer<typeof baseSchema> & {
  provider: 'sendgrid';
  config: z.infer<typeof sendgridConfigSchema>;
};
type PostmarkFormData = z.infer<typeof baseSchema> & {
  provider: 'postmark';
  config: z.infer<typeof postmarkConfigSchema>;
};
type TwilioFormData = z.infer<typeof baseSchema> & {
  provider: 'twilio';
  config: z.infer<typeof twilioConfigSchema>;
};

type ChannelFormData = SmtpFormData | SendgridFormData | PostmarkFormData | TwilioFormData;

// Masked password placeholder
const MASKED_PASSWORD = '••••••••';

// Form section component for visual hierarchy
function FormSection({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-500">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function ChannelsSettingsPage() {
  const { addToast } = useToast();
  const { data: meData, isLoading: meLoading } = useMe();

  // Check admin role
  const isAdmin = meData?.user?.role === AuthRole.RoleAdmin;

  const [selectedChannel, setSelectedChannel] = useState<ChannelsChannelResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<ChannelsChannelResponse | null>(null);
  const [selectedProvider, setSelectedProvider] =
    useState<ChannelsCreateChannelRequestProvider | null>(null);
  const [isProviderSelectOpen, setIsProviderSelectOpen] = useState(false);
  const [testingChannelId, setTestingChannelId] = useState<string | null>(null);
  const [testPhoneModal, setTestPhoneModal] = useState<{
    isOpen: boolean;
    channel: ChannelsChannelResponse | null;
  }>({ isOpen: false, channel: null });
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testMessage, setTestMessage] = useState('');

  const { data, isLoading, isError, refetch } = useListChannels();

  const { mutate: createChannel, isPending: isCreating } = useCreateChannel({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Channel created', variant: 'success' });
        setIsModalOpen(false);
        setSelectedProvider(null);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to create channel', variant: 'error' });
      },
    },
  });

  const { mutate: updateChannel, isPending: isUpdating } = useUpdateChannel({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Channel updated', variant: 'success' });
        setIsModalOpen(false);
        setSelectedChannel(null);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to update channel', variant: 'error' });
      },
    },
  });

  const { mutate: deleteChannel, isPending: isDeleting } = useDeleteChannel({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Channel deleted', variant: 'success' });
        setIsDeleteConfirmOpen(false);
        setChannelToDelete(null);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to delete channel', variant: 'error' });
      },
    },
  });

  const { mutate: testChannel } = useTestChannel({
    mutation: {
      onSuccess: (response) => {
        if (response.success) {
          addToast({ message: response.message ?? 'Test message sent', variant: 'success' });
        } else {
          addToast({
            message: response.message ?? 'Test failed',
            variant: 'error',
          });
        }
        setTestingChannelId(null);
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to send test', variant: 'error' });
        setTestingChannelId(null);
      },
    },
  });

  // Group channels by type
  const groupedChannels = useMemo(() => {
    const channels = data?.channels ?? [];
    const grouped: Record<string, ChannelsChannelResponse[]> = {
      email: [],
      sms: [],
    };

    channels.forEach((channel) => {
      const type = channel.channelType ?? 'email';
      if (grouped[type]) {
        grouped[type].push(channel);
      }
    });

    // Sort by priority within each group
    Object.keys(grouped).forEach((type) => {
      const channels = grouped[type];
      if (channels) {
        channels.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
      }
    });

    return grouped;
  }, [data?.channels]);

  const handleOpenCreate = () => {
    setSelectedChannel(null);
    setIsProviderSelectOpen(true);
  };

  const handleSelectProvider = (provider: ChannelsCreateChannelRequestProvider) => {
    setSelectedProvider(provider);
    setIsProviderSelectOpen(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (channel: ChannelsChannelResponse) => {
    setSelectedChannel(channel);
    setSelectedProvider(channel.provider as ChannelsCreateChannelRequestProvider);
    setIsModalOpen(true);
  };

  const handleOpenDelete = (channel: ChannelsChannelResponse) => {
    setChannelToDelete(channel);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (channelToDelete?.id) {
      deleteChannel({ id: channelToDelete.id });
    }
  };

  const handleToggleEnabled = (channel: ChannelsChannelResponse) => {
    if (!channel.id) return;
    updateChannel({
      id: channel.id,
      data: {
        displayName: channel.displayName ?? '',
        isEnabled: !channel.isEnabled,
        priority: channel.priority,
        channelType: channel.channelType as ChannelsCreateChannelRequestChannelType,
        provider: channel.provider as ChannelsCreateChannelRequestProvider,
        config: channel.config ?? {},
      },
    });
  };

  const handleTest = (channel: ChannelsChannelResponse) => {
    if (!channel.id) return;

    // For SMS channels, prompt for phone number first
    if (channel.channelType === 'sms') {
      setTestPhoneModal({ isOpen: true, channel });
      return;
    }

    // For email channels, test immediately (uses logged-in user's email)
    setTestingChannelId(channel.id);
    testChannel({ id: channel.id, data: {} });
  };

  const handleTestSmsSubmit = () => {
    const channel = testPhoneModal.channel;
    if (!channel?.id || !testPhoneNumber.trim()) return;

    setTestPhoneModal({ isOpen: false, channel: null });
    setTestingChannelId(channel.id);
    testChannel({
      id: channel.id,
      data: {
        phoneNumber: testPhoneNumber.trim(),
        message: testMessage.trim() || undefined,
      },
    });
    setTestPhoneNumber('');
    setTestMessage('');
  };

  // Show loading while checking auth
  if (meLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  // Show access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.SETTINGS}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Communication Channels
            </h1>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 p-3 dark:bg-red-500/10">
            <Shield className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
            You need administrator privileges to access this section.
          </p>
          <Link to={ROUTES.SETTINGS}>
            <Button className="mt-4" variant="secondary">
              Back to Settings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.SETTINGS}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Communication Channels
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Configure email and SMS providers for notifications
            </p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} leftIcon={<Plus className="h-4 w-4" />}>
          Add Channel
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load channel configurations
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      )}

      {/* Channels list grouped by type */}
      {!isLoading && !isError && (
        <>
          {Object.entries(groupedChannels).map(([type, channels]) => {
            const typeInfo = CHANNEL_TYPE_INFO[type as ChannelsCreateChannelRequestChannelType];
            return (
              <div key={type}>
                <h2 className="mb-4 flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                  {typeInfo?.icon}
                  {typeInfo?.label ?? type}
                </h2>
                {channels.length > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
                    {channels.map((channel, index) => (
                      <ChannelRow
                        key={channel.id}
                        channel={channel}
                        index={index}
                        isPrimary={index === 0}
                        onEdit={() => handleOpenEdit(channel)}
                        onDelete={() => handleOpenDelete(channel)}
                        onToggle={() => handleToggleEnabled(channel)}
                        onTest={() => handleTest(channel)}
                        isTesting={testingChannelId === channel.id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
                    <p className="text-sm text-gray-500 dark:text-zinc-500">
                      No {typeInfo?.label.toLowerCase()} channels configured
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty state when no channels at all */}
          {data?.channels?.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-100 p-3 dark:bg-zinc-800">
                <Mail className="h-6 w-6 text-gray-400 dark:text-zinc-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                No channels configured
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
                Add a communication channel to enable notifications
              </p>
              <Button
                className="mt-4"
                onClick={handleOpenCreate}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add Channel
              </Button>
            </div>
          )}
        </>
      )}

      {/* Provider Selection Modal */}
      <Modal
        isOpen={isProviderSelectOpen}
        onClose={() => setIsProviderSelectOpen(false)}
        title="Select Provider"
        subtitle="Choose a provider type for your new channel"
      >
        <div className="space-y-2">
          {Object.entries(PROVIDER_INFO).map(([provider, info]) => (
            <button
              key={provider}
              onClick={() => handleSelectProvider(provider as ChannelsCreateChannelRequestProvider)}
              className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-4 text-left transition-all hover:border-blue-500 hover:bg-blue-50 dark:border-white/[0.06] dark:hover:border-blue-500/50 dark:hover:bg-blue-500/5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
                {CHANNEL_TYPE_INFO[info.channelType]?.icon}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{info.label}</div>
                <div className="text-sm text-gray-500 dark:text-zinc-500">
                  {CHANNEL_TYPE_INFO[info.channelType]?.label}
                </div>
              </div>
            </button>
          ))}
        </div>
      </Modal>

      {/* Create/Edit Modal */}
      {selectedProvider && (
        <ChannelModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedChannel(null);
            setSelectedProvider(null);
          }}
          channel={selectedChannel}
          provider={selectedProvider}
          onSubmit={(formData) => {
            if (selectedChannel?.id) {
              updateChannel({
                id: selectedChannel.id,
                data: {
                  displayName: formData.displayName,
                  isEnabled: formData.isEnabled,
                  priority: formData.priority,
                  channelType: PROVIDER_INFO[formData.provider].channelType,
                  provider: formData.provider,
                  config: formData.config,
                },
              });
            } else {
              createChannel({
                data: {
                  displayName: formData.displayName,
                  isEnabled: formData.isEnabled,
                  priority: formData.priority,
                  channelType: PROVIDER_INFO[formData.provider].channelType,
                  provider: formData.provider,
                  config: formData.config,
                },
              });
            }
          }}
          isSaving={isCreating || isUpdating}
        />
      )}

      {/* Delete Confirmation */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setChannelToDelete(null);
        }}
        title="Delete Channel"
        subtitle="This action cannot be undone"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setChannelToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Are you sure you want to delete <strong>{channelToDelete?.displayName}</strong>? This will
          remove the channel configuration permanently.
        </p>
      </Modal>

      {/* SMS Test Phone Number Modal */}
      <Modal
        isOpen={testPhoneModal.isOpen}
        onClose={() => {
          setTestPhoneModal({ isOpen: false, channel: null });
          setTestPhoneNumber('');
          setTestMessage('');
        }}
        title="Test SMS Channel"
        subtitle={`Send a test message via ${testPhoneModal.channel?.displayName}`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setTestPhoneModal({ isOpen: false, channel: null });
                setTestPhoneNumber('');
                setTestMessage('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTestSmsSubmit}
              disabled={!testPhoneNumber.trim()}
              leftIcon={<Send className="h-4 w-4" />}
            >
              Send Test
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Phone Number"
            value={testPhoneNumber}
            onChange={(e) => setTestPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            hint="E.164 format recommended"
          />
          <TextField
            label="Message"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Your test message here..."
            hint="Leave empty for default test message"
          />
        </div>
      </Modal>
    </div>
  );
}

// Channel Row Component
function ChannelRow({
  channel,
  index,
  isPrimary,
  onEdit,
  onDelete,
  onToggle,
  onTest,
  isTesting,
}: {
  channel: ChannelsChannelResponse;
  index: number;
  isPrimary: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onTest: () => void;
  isTesting: boolean;
}) {
  const providerInfo =
    PROVIDER_INFO[channel.provider as ChannelsCreateChannelRequestProvider] ?? null;

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-gray-400 dark:text-zinc-600">
          <GripVertical className="h-4 w-4" />
          <span className="w-6 text-center text-sm font-medium">{(channel.priority ?? 0) + 1}</span>
        </div>
        <Toggle label="" checked={channel.isEnabled ?? false} onChange={onToggle} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-medium text-gray-900 dark:text-white">
              {channel.displayName}
            </span>
            <Badge variant="default" size="sm">
              {providerInfo?.label ?? channel.provider}
            </Badge>
            {isPrimary && channel.isEnabled && (
              <Badge variant="success" size="sm">
                Primary
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
            Priority {(channel.priority ?? 0) + 1} • {channel.isEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onTest}
          loading={isTesting}
          leftIcon={<Send className="h-4 w-4" />}
          disabled={!channel.isEnabled}
        >
          Test
        </Button>
        <button
          onClick={onEdit}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-zinc-300"
          title="Edit"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Channel Form Modal
function ChannelModal({
  isOpen,
  onClose,
  channel,
  provider,
  onSubmit,
  isSaving,
}: {
  isOpen: boolean;
  onClose: () => void;
  channel: ChannelsChannelResponse | null;
  provider: ChannelsCreateChannelRequestProvider;
  onSubmit: (data: ChannelFormData) => void;
  isSaving: boolean;
}) {
  const providerInfo = PROVIDER_INFO[provider];
  const isEdit = !!channel;

  // Get the right schema based on provider
  const getSchema = () => {
    switch (provider) {
      case 'smtp':
        return baseSchema.extend({ provider: z.literal('smtp'), config: smtpConfigSchema });
      case 'sendgrid':
        return baseSchema.extend({ provider: z.literal('sendgrid'), config: sendgridConfigSchema });
      case 'postmark':
        return baseSchema.extend({ provider: z.literal('postmark'), config: postmarkConfigSchema });
      case 'twilio':
        return baseSchema.extend({ provider: z.literal('twilio'), config: twilioConfigSchema });
      default:
        return baseSchema.extend({ provider: z.literal('smtp'), config: smtpConfigSchema });
    }
  };

  const getDefaultValues = useCallback((): ChannelFormData => {
    const baseDefaults = {
      displayName: channel?.displayName ?? '',
      priority: channel?.priority ?? 0,
      isEnabled: channel?.isEnabled ?? true,
    };

    const config = channel?.config ?? {};

    switch (provider) {
      case 'smtp':
        return {
          ...baseDefaults,
          provider: 'smtp',
          config: {
            host: (config['host'] as string) ?? '',
            port: (config['port'] as number) ?? 587,
            username: (config['username'] as string) ?? '',
            password: '',
            fromAddress: (config['fromAddress'] as string) ?? '',
            fromName: (config['fromName'] as string) ?? '',
            useTLS: (config['useTLS'] as boolean) ?? true,
          },
        };
      case 'sendgrid':
        return {
          ...baseDefaults,
          provider: 'sendgrid',
          config: {
            apiKey: '',
            fromAddress: (config['fromAddress'] as string) ?? '',
            fromName: (config['fromName'] as string) ?? '',
          },
        };
      case 'postmark':
        return {
          ...baseDefaults,
          provider: 'postmark',
          config: {
            serverToken: '',
            fromAddress: (config['fromAddress'] as string) ?? '',
            fromName: (config['fromName'] as string) ?? '',
          },
        };
      case 'twilio': {
        // Determine auth method from existing config
        const hasApiKey = !!(config['apiKeySid'] as string);
        const authMethod = hasApiKey ? 'apikey' : 'account';
        return {
          ...baseDefaults,
          provider: 'twilio',
          config: {
            authMethod: authMethod as 'account' | 'apikey',
            accountSid: (config['accountSid'] as string) ?? '',
            authToken: '',
            apiKeySid: (config['apiKeySid'] as string) ?? '',
            apiKeySecret: '',
            region: (config['region'] as 'us1' | 'ie1' | 'au1') ?? 'us1',
            senderType: (config['senderType'] as 'number' | 'messaging_service') ?? 'number',
            fromNumber: (config['fromNumber'] as string) ?? '',
            messagingServiceSid: (config['messagingServiceSid'] as string) ?? '',
          },
        };
      }
    }
  }, [channel, provider]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChannelFormData>({
    resolver: zodResolver(getSchema()),
    defaultValues: getDefaultValues(),
  });

  // Reset form when modal opens or provider changes
  useEffect(() => {
    if (isOpen) {
      reset(getDefaultValues());
    }
  }, [isOpen, getDefaultValues, reset]);

  const handleFormSubmit = (data: ChannelFormData) => {
    // Clean up config - remove empty password fields when editing
    const cleanedConfig = { ...data.config };

    if (isEdit) {
      // When editing, if password fields are empty, don't send them to preserve existing values
      if ('password' in cleanedConfig && !cleanedConfig.password) {
        delete cleanedConfig.password;
      }
      if ('apiKey' in cleanedConfig && !cleanedConfig.apiKey) {
        delete cleanedConfig.apiKey;
      }
      if ('serverToken' in cleanedConfig && !cleanedConfig.serverToken) {
        delete cleanedConfig.serverToken;
      }
      if ('authToken' in cleanedConfig && !cleanedConfig.authToken) {
        delete cleanedConfig.authToken;
      }
    }

    onSubmit({ ...data, config: cleanedConfig } as ChannelFormData);
  };

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Channel' : `Add ${providerInfo.label} Channel`}
      subtitle={`Configure ${providerInfo.label} settings`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="channel-form" loading={isSaving}>
            {isEdit ? 'Save Changes' : 'Create Channel'}
          </Button>
        </>
      }
    >
      <form id="channel-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Header section with name and enabled toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <TextField
              label="Display Name"
              {...register('displayName')}
              error={errors.displayName?.message}
              placeholder={`My ${providerInfo.label} Channel`}
            />
          </div>
          <div className="pt-7">
            <Controller
              name="isEnabled"
              control={control}
              render={({ field }) => (
                <Toggle label="" checked={field.value} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Provider-specific fields */}
        {provider === 'smtp' && (
          <SmtpFields
            register={register as never}
            errors={errors}
            control={control as never}
            isEdit={isEdit}
          />
        )}
        {provider === 'sendgrid' && (
          <SendgridFields register={register as never} errors={errors} isEdit={isEdit} />
        )}
        {provider === 'postmark' && (
          <PostmarkFields register={register as never} errors={errors} isEdit={isEdit} />
        )}
        {provider === 'twilio' && (
          <TwilioFields errors={errors} control={control as never} isEdit={isEdit} />
        )}
      </form>
    </SlideOutPanel>
  );
}

// SMTP Fields
function SmtpFields({
  register,
  errors,
  control,
  isEdit,
}: {
  register: ReturnType<typeof useForm<SmtpFormData>>['register'];
  errors: ReturnType<typeof useForm<SmtpFormData>>['formState']['errors'];
  control: ReturnType<typeof useForm<SmtpFormData>>['control'];
  isEdit: boolean;
}) {
  return (
    <div className="space-y-6">
      <FormSection title="Server">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Host"
            {...register('config.host')}
            error={
              (errors as SmtpFormData['config'] & { config?: { host?: { message?: string } } })
                ?.config?.host?.message
            }
            placeholder="smtp.example.com"
          />
          <TextField
            label="Port"
            type="number"
            {...register('config.port', { valueAsNumber: true })}
            error={
              (errors as SmtpFormData['config'] & { config?: { port?: { message?: string } } })
                ?.config?.port?.message
            }
            placeholder="587"
          />
        </div>
        <Controller
          name="config.useTLS"
          control={control}
          render={({ field }) => (
            <Checkbox label="Use TLS encryption" checked={field.value} onChange={field.onChange} />
          )}
        />
      </FormSection>

      <FormSection title="Authentication">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Username"
            {...register('config.username')}
            error={
              (errors as SmtpFormData['config'] & { config?: { username?: { message?: string } } })
                ?.config?.username?.message
            }
            placeholder="Optional"
          />
          <PasswordField
            label="Password"
            {...register('config.password')}
            error={
              (errors as SmtpFormData['config'] & { config?: { password?: { message?: string } } })
                ?.config?.password?.message
            }
            placeholder={isEdit ? MASKED_PASSWORD : 'Optional'}
            showLockIcon={false}
          />
        </div>
      </FormSection>

      <FormSection title="Sender">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="From Address"
            type="email"
            {...register('config.fromAddress')}
            error={
              (
                errors as SmtpFormData['config'] & {
                  config?: { fromAddress?: { message?: string } };
                }
              )?.config?.fromAddress?.message
            }
            placeholder="noreply@example.com"
          />
          <TextField
            label="From Name"
            {...register('config.fromName')}
            error={
              (errors as SmtpFormData['config'] & { config?: { fromName?: { message?: string } } })
                ?.config?.fromName?.message
            }
            placeholder="Smart Home"
          />
        </div>
      </FormSection>
    </div>
  );
}

// SendGrid Fields
function SendgridFields({
  register,
  errors,
  isEdit,
}: {
  register: ReturnType<typeof useForm<SendgridFormData>>['register'];
  errors: ReturnType<typeof useForm<SendgridFormData>>['formState']['errors'];
  isEdit: boolean;
}) {
  return (
    <div className="space-y-6">
      <FormSection title="API Credentials">
        <PasswordField
          label="API Key"
          {...register('config.apiKey')}
          error={
            (errors as SendgridFormData['config'] & { config?: { apiKey?: { message?: string } } })
              ?.config?.apiKey?.message
          }
          placeholder={isEdit ? MASKED_PASSWORD : 'SG.xxx...'}
          showLockIcon={false}
        />
      </FormSection>

      <FormSection title="Sender">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="From Address"
            type="email"
            {...register('config.fromAddress')}
            error={
              (
                errors as SendgridFormData['config'] & {
                  config?: { fromAddress?: { message?: string } };
                }
              )?.config?.fromAddress?.message
            }
            placeholder="noreply@example.com"
          />
          <TextField
            label="From Name"
            {...register('config.fromName')}
            error={
              (
                errors as SendgridFormData['config'] & {
                  config?: { fromName?: { message?: string } };
                }
              )?.config?.fromName?.message
            }
            placeholder="Smart Home"
          />
        </div>
      </FormSection>
    </div>
  );
}

// Postmark Fields
function PostmarkFields({
  register,
  errors,
  isEdit,
}: {
  register: ReturnType<typeof useForm<PostmarkFormData>>['register'];
  errors: ReturnType<typeof useForm<PostmarkFormData>>['formState']['errors'];
  isEdit: boolean;
}) {
  return (
    <div className="space-y-6">
      <FormSection title="API Credentials">
        <PasswordField
          label="Server Token"
          {...register('config.serverToken')}
          error={
            (
              errors as PostmarkFormData['config'] & {
                config?: { serverToken?: { message?: string } };
              }
            )?.config?.serverToken?.message
          }
          placeholder={isEdit ? MASKED_PASSWORD : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
          showLockIcon={false}
        />
      </FormSection>

      <FormSection title="Sender">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="From Address"
            type="email"
            {...register('config.fromAddress')}
            error={
              (
                errors as PostmarkFormData['config'] & {
                  config?: { fromAddress?: { message?: string } };
                }
              )?.config?.fromAddress?.message
            }
            placeholder="noreply@example.com"
          />
          <TextField
            label="From Name"
            {...register('config.fromName')}
            error={
              (
                errors as PostmarkFormData['config'] & {
                  config?: { fromName?: { message?: string } };
                }
              )?.config?.fromName?.message
            }
            placeholder="Smart Home"
          />
        </div>
      </FormSection>
    </div>
  );
}

// Styled toggle button group for clean option selection
function ToggleButtonGroup({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-800/50">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
            value === option.value
              ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
              : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Twilio Fields
function TwilioFields({
  errors,
  control,
  isEdit,
}: {
  errors: ReturnType<typeof useForm<TwilioFormData>>['formState']['errors'];
  control: ReturnType<typeof useForm<TwilioFormData>>['control'];
  isEdit: boolean;
}) {
  const { addToast } = useToast();
  const [messagingServices, setMessagingServices] = useState<
    { sid: string; friendlyName: string }[]
  >([]);
  const [isFetchingServices, setIsFetchingServices] = useState(false);

  const { mutate: fetchMessagingServices } = useListTwilioMessagingServices({
    mutation: {
      retry: false,
      onSuccess: (data) => {
        const services = (data.services ?? [])
          .filter((s): s is { sid: string; friendlyName: string } => !!s.sid && !!s.friendlyName)
          .map((s) => ({ sid: s.sid, friendlyName: s.friendlyName }));
        setMessagingServices(services);
        setIsFetchingServices(false);
        if (!services.length) {
          addToast({ message: 'No messaging services found', variant: 'warning' });
        }
      },
      onError: (err: Error) => {
        setIsFetchingServices(false);
        addToast({ message: err.message ?? 'Failed to fetch services', variant: 'error' });
      },
    },
  });

  return (
    <Controller
      name="config"
      control={control}
      render={({ field }) => {
        const config = field.value;
        const authMethod = config.authMethod ?? 'account';
        const senderType = config.senderType ?? 'number';

        const updateConfig = (updates: Partial<typeof config>) => {
          field.onChange({ ...config, ...updates });
        };

        const handleFetchServices = () => {
          const hasCredentials =
            authMethod === 'apikey'
              ? config.apiKeySid && config.apiKeySecret
              : config.accountSid && config.authToken;
          if (!hasCredentials) {
            addToast({ message: 'Enter credentials first', variant: 'error' });
            return;
          }
          setIsFetchingServices(true);
          fetchMessagingServices({
            data: {
              accountSid: config.accountSid,
              authToken: config.authToken,
              apiKeySid: config.apiKeySid,
              apiKeySecret: config.apiKeySecret,
              region: config.region,
            },
          });
        };

        return (
          <div className="space-y-6">
            {/* Account Section */}
            <FormSection title="Account">
              <TextField
                label="Account SID"
                value={config.accountSid ?? ''}
                onChange={(e) => updateConfig({ accountSid: e.target.value })}
                error={
                  (
                    errors as TwilioFormData['config'] & {
                      config?: { accountSid?: { message?: string } };
                    }
                  )?.config?.accountSid?.message
                }
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <Select
                label="Region"
                value={config.region ?? 'us1'}
                onChange={(value) => updateConfig({ region: value as 'us1' | 'ie1' | 'au1' })}
                options={TWILIO_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
              />
            </FormSection>

            {/* Authentication Section */}
            <FormSection title="Authentication">
              <div className="space-y-4">
                <ToggleButtonGroup
                  value={authMethod}
                  onChange={(v) => updateConfig({ authMethod: v as 'account' | 'apikey' })}
                  options={[
                    { value: 'account', label: 'Auth Token' },
                    { value: 'apikey', label: 'API Key' },
                  ]}
                />
                {authMethod === 'apikey' ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="API Key SID"
                      value={config.apiKeySid ?? ''}
                      onChange={(e) => updateConfig({ apiKeySid: e.target.value })}
                      placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                    <PasswordField
                      label="API Key Secret"
                      value={config.apiKeySecret ?? ''}
                      onChange={(e) => updateConfig({ apiKeySecret: e.target.value })}
                      placeholder={isEdit ? MASKED_PASSWORD : 'Enter secret'}
                      showLockIcon={false}
                    />
                  </div>
                ) : (
                  <PasswordField
                    label="Auth Token"
                    value={config.authToken ?? ''}
                    onChange={(e) => updateConfig({ authToken: e.target.value })}
                    error={
                      (
                        errors as TwilioFormData['config'] & {
                          config?: { authToken?: { message?: string } };
                        }
                      )?.config?.authToken?.message
                    }
                    placeholder={isEdit ? MASKED_PASSWORD : 'Enter token'}
                    showLockIcon={false}
                  />
                )}
              </div>
            </FormSection>

            {/* Sender Section */}
            <FormSection title="Sender">
              <div className="space-y-4">
                <ToggleButtonGroup
                  value={senderType}
                  onChange={(v) =>
                    updateConfig({
                      senderType: v as 'number' | 'messaging_service',
                      ...(v === 'number' ? { messagingServiceSid: '' } : { fromNumber: '' }),
                    })
                  }
                  options={[
                    { value: 'number', label: 'Phone Number' },
                    { value: 'messaging_service', label: 'Messaging Service' },
                  ]}
                />
                {senderType === 'number' ? (
                  <TextField
                    label="From Number"
                    value={config.fromNumber ?? ''}
                    onChange={(e) => updateConfig({ fromNumber: e.target.value })}
                    error={
                      (
                        errors as TwilioFormData['config'] & {
                          config?: { fromNumber?: { message?: string } };
                        }
                      )?.config?.fromNumber?.message
                    }
                    placeholder="+1234567890"
                  />
                ) : (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Select
                        label="Messaging Service"
                        value={config.messagingServiceSid ?? ''}
                        onChange={(value) => updateConfig({ messagingServiceSid: value })}
                        options={[
                          { value: '', label: 'Select a service...' },
                          ...messagingServices.map((s) => ({
                            value: s.sid,
                            label: s.friendlyName,
                          })),
                        ]}
                        error={
                          (
                            errors as TwilioFormData['config'] & {
                              config?: { messagingServiceSid?: { message?: string } };
                            }
                          )?.config?.messagingServiceSid?.message
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleFetchServices}
                      loading={isFetchingServices}
                      leftIcon={<RefreshCw className="h-4 w-4" />}
                    >
                      Fetch
                    </Button>
                  </div>
                )}
              </div>
            </FormSection>
          </div>
        );
      }}
    />
  );
}
